import React, { useContext, useEffect, useMemo, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ReplayIcon from "@material-ui/icons/Replay";
import SearchIcon from "@material-ui/icons/Search";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  pageCard: {
    borderRadius: 24,
    padding: theme.spacing(2.5),
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  infoBox: {
    borderRadius: 18,
    padding: theme.spacing(2),
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(31,84,132,0.05)",
    marginBottom: theme.spacing(2)
  },
  tableWrap: {
    overflowX: "auto"
  },
  rowTitle: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 220
  },
  codeBlock: {
    fontFamily: "Consolas, monospace",
    fontSize: "0.8rem",
    wordBreak: "break-all"
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  }
}));

const emptyForm = {
  id: null,
  businessCustomerId: "",
  customerEstablishmentId: "",
  hardwareId: "",
  baseboardSerial: "",
  biosSerial: "",
  systemUuid: "",
  primaryMac: "",
  diskSerial: "",
  hostname: "",
  agentVersion: "",
  installerVersion: "",
  isActive: true,
  isAuthorized: true,
  licenseStatus: "active",
  notes: ""
};

const buildClientLabel = client => {
  if (!client) return "";
  const name = String(client.name || "").trim();
  const identification = String(client.identificationNumber || "").trim();
  return identification ? `${name} - ${identification}` : name || `Cliente #${client.id}`;
};

const buildEstablishmentLabel = establishment => {
  if (!establishment) return "";
  const name = String(establishment.name || "").trim();
  const code = String(establishment.installationCode || "").trim();
  return code ? `${name} - ${code}` : name || `Sucursal #${establishment.id}`;
};

const ClientHardwareAuthorizations = () => {
  const classes = useStyles();
  useContext(AuthContext);

  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [debouncedSearchParam, setDebouncedSearchParam] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [clients, setClients] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);

  const totalPages = useMemo(() => {
    if (pageSize === -1) {
      return 1;
    }
    return Math.max(Math.ceil(count / pageSize), 1);
  }, [count, pageSize]);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/clients/hardware-authorizations", {
        params: {
          searchParam: debouncedSearchParam,
          activeFilter,
          pageNumber,
          pageSize: pageSize === -1 ? "all" : pageSize
        }
      });

      setRecords(Array.isArray(data?.records) ? data.records : []);
      setCount(Number(data?.count || 0));
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const { data } = await api.get("/clients/list");
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      toastError(error);
    }
  };

  const loadEstablishments = async businessCustomerId => {
    if (!businessCustomerId) {
      setEstablishments([]);
      return;
    }

    try {
      const { data } = await api.get(`/clients/${businessCustomerId}/establishments/list`);
      setEstablishments(Array.isArray(data) ? data : []);
    } catch (error) {
      toastError(error);
      setEstablishments([]);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchParam(searchParam);
      setPageNumber(1);
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchParam]);

  useEffect(() => {
    setPageNumber(1);
  }, [activeFilter, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [debouncedSearchParam, activeFilter, pageNumber, pageSize]);

  const openCreateModal = async () => {
    setForm(emptyForm);
    setEstablishments([]);
    setShowAdvancedFields(false);
    setModalOpen(true);
  };

  const openEditModal = async record => {
    setForm({
      id: record.id,
      businessCustomerId: record.businessCustomerId || "",
      customerEstablishmentId: record.customerEstablishmentId || "",
      hardwareId: record.hardwareId || "",
      baseboardSerial: record.baseboardSerial || "",
      biosSerial: record.biosSerial || "",
      systemUuid: record.systemUuid || "",
      primaryMac: record.primaryMac || "",
      diskSerial: record.diskSerial || "",
      hostname: record.hostname || "",
      agentVersion: record.agentVersion || "",
      installerVersion: record.installerVersion || "",
      isActive: record.isActive !== false,
      isAuthorized: record.isAuthorized !== false,
      licenseStatus: record.licenseStatus || "active",
      notes: record.notes || ""
    });
    setShowAdvancedFields(Boolean(
      record.baseboardSerial ||
      record.biosSerial ||
      record.systemUuid ||
      record.primaryMac ||
      record.diskSerial ||
      record.hostname ||
      record.agentVersion ||
      record.installerVersion ||
      record.notes
    ));
    await loadEstablishments(record.businessCustomerId);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        businessCustomerId: form.businessCustomerId || null,
        customerEstablishmentId: form.customerEstablishmentId || null
      };

      if (!payload.businessCustomerId) {
        toast.error("Debe seleccionar un cliente.");
        return;
      }

      if (!String(payload.hardwareId || "").trim()) {
        toast.error("Debe ingresar el hardware ID.");
        return;
      }

      setSaving(true);
      if (form.id) {
        await api.put(`/clients/hardware-authorizations/${form.id}`, payload);
        toast.success("Autorizacion de hardware actualizada.");
      } else {
        await api.post("/clients/hardware-authorizations", payload);
        toast.success("Autorizacion de hardware creada.");
      }
      setModalOpen(false);
      await loadRecords();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async record => {
    const confirmed = window.confirm(
      `Va a eliminar la autorizacion de hardware para "${record.displayName}".`
    );

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/clients/hardware-authorizations/${record.id}`);
      toast.success("Autorizacion eliminada.");
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const selectedClient = clients.find(item => Number(item.id) === Number(form.businessCustomerId)) || null;
  const selectedEstablishment =
    establishments.find(item => Number(item.id) === Number(form.customerEstablishmentId)) || null;
  const historicalCode =
    selectedEstablishment?.installationCode ||
    selectedClient?.installationCode ||
    "";

  return (
    <MainContainer>
      <MainHeader>
        <Title>Licenciamiento agente</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={loadRecords}
            disabled={loading}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
          >
            Nueva autorizacion
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.pageCard}>
        <Box className={classes.infoBox}>
          <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
            Administracion de hardware autorizado
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Aqui se relacionan equipos autorizados con clientes o sucursales, usando el
            identificador historico ya existente y la nueva huella de hardware para instalacion,
            migracion y licenciamiento futuro.
          </Typography>
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              variant="outlined"
              label="Buscar"
              value={searchParam}
              onChange={e => setSearchParam(e.target.value)}
              placeholder="Hardware ID, hostname, cliente, sucursal, identificacion o codigo historico"
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Estado</InputLabel>
              <Select
                value={activeFilter}
                onChange={e => setActiveFilter(e.target.value)}
                label="Estado"
              >
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchParam("");
                setActiveFilter("active");
                setPageNumber(1);
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>

        <div className={classes.tableWrap}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente / sucursal</TableCell>
                <TableCell>Hardware</TableCell>
                <TableCell>Codigo historico</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Ultima validacion</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.id}>
                  <TableCell>
                    <div className={classes.rowTitle}>
                      <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                        {record.displayName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {record.businessCustomer?.identificationNumber || "Sin identificacion"}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.codeBlock}>{record.hardwareId}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {record.hostname || "Sin hostname"}
                    </Typography>
                  </TableCell>
                  <TableCell>{record.historicalCode || "Sin codigo"}</TableCell>
                  <TableCell>
                    <div className={classes.chipRow}>
                      <Chip
                        size="small"
                        label={record.isActive ? "Activo" : "Inactivo"}
                        style={{
                          backgroundColor: record.isActive ? "rgba(29,154,108,0.14)" : "rgba(214,69,69,0.14)",
                          color: record.isActive ? "#1D9A6C" : "#D64545",
                          fontWeight: 700
                        }}
                      />
                      <Chip
                        size="small"
                        label={record.isAuthorized ? "Autorizado" : "Bloqueado"}
                        style={{
                          backgroundColor: record.isAuthorized ? "rgba(28,78,125,0.14)" : "rgba(125,45,28,0.14)",
                          color: record.isAuthorized ? "#1c4e7d" : "#8a3b2e",
                          fontWeight: 700
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    {record.lastValidatedAt
                      ? new Date(record.lastValidatedAt).toLocaleString("es-CO")
                      : "Sin validacion"}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gridGap={8}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => openEditModal(record)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDelete(record)}
                      >
                        Eliminar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!records.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography align="center" color="textSecondary">
                      No hay autorizaciones registradas.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          component="div"
          count={count}
          page={Math.max(pageNumber - 1, 0)}
          onPageChange={(_e, nextPage) => setPageNumber(nextPage + 1)}
          rowsPerPage={pageSize === -1 ? count || 1 : pageSize}
          onRowsPerPageChange={event => {
            const value = Number(event.target.value);
            setPageSize(value);
            setPageNumber(1);
          }}
          rowsPerPageOptions={[
            { label: "20", value: 20 },
            { label: "50", value: 50 },
            { label: "100", value: 100 },
            { label: "Todos", value: -1 }
          ]}
          labelRowsPerPage="Mostrar"
          labelDisplayedRows={({ from, to, count: total }) => `${from}-${to} de ${total}`}
        />
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{form.id ? "Editar autorizacion de hardware" : "Nueva autorizacion de hardware"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={clients}
                value={selectedClient}
                onChange={async (_event, value) => {
                  const nextClientId = value ? Number(value.id) : "";
                  setForm(prev => ({
                    ...prev,
                    businessCustomerId: nextClientId,
                    customerEstablishmentId: ""
                  }));
                  await loadEstablishments(nextClientId);
                }}
                getOptionLabel={option => buildClientLabel(option)}
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                noOptionsText="No hay clientes disponibles"
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Cliente"
                    placeholder="Busque por nombre, NIT o codigo"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={establishments}
                value={selectedEstablishment}
                onChange={(_event, value) =>
                  setForm(prev => ({
                    ...prev,
                    customerEstablishmentId: value ? Number(value.id) : ""
                  }))
                }
                getOptionLabel={option => buildEstablishmentLabel(option)}
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                noOptionsText={form.businessCustomerId ? "No hay sucursales disponibles" : "Seleccione primero un cliente"}
                disabled={!form.businessCustomerId}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Sucursal"
                    placeholder="Sin sucursal o busque una sede"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Codigo historico"
                value={historicalCode}
                InputProps={{ readOnly: true }}
                helperText="Se toma del cliente o de la sucursal relacionada."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Hardware ID"
                value={form.hardwareId}
                onChange={e => setForm(prev => ({ ...prev, hardwareId: e.target.value }))}
                helperText="Entre 24 y 128 caracteres. Se normaliza en mayusculas y sin espacios."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Autorizacion</InputLabel>
                <Select
                  value={form.isAuthorized ? "yes" : "no"}
                  onChange={e => setForm(prev => ({ ...prev, isAuthorized: e.target.value === "yes" }))}
                  label="Autorizacion"
                >
                  <MenuItem value="yes">Autorizado</MenuItem>
                  <MenuItem value="no">Bloqueado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowAdvancedFields(prev => !prev)}
              >
                {showAdvancedFields ? "Ocultar datos avanzados" : "Mostrar datos avanzados"}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Collapse in={showAdvancedFields}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" label="Baseboard serial" value={form.baseboardSerial} onChange={e => setForm(prev => ({ ...prev, baseboardSerial: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" label="BIOS serial" value={form.biosSerial} onChange={e => setForm(prev => ({ ...prev, biosSerial: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" label="System UUID" value={form.systemUuid} onChange={e => setForm(prev => ({ ...prev, systemUuid: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" label="MAC principal" value={form.primaryMac} onChange={e => setForm(prev => ({ ...prev, primaryMac: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" label="Disk serial" value={form.diskSerial} onChange={e => setForm(prev => ({ ...prev, diskSerial: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" label="Hostname" value={form.hostname} onChange={e => setForm(prev => ({ ...prev, hostname: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" label="Version agente" value={form.agentVersion} onChange={e => setForm(prev => ({ ...prev, agentVersion: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" label="Version instalador" value={form.installerVersion} onChange={e => setForm(prev => ({ ...prev, installerVersion: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Registro activo</InputLabel>
                      <Select
                        value={form.isActive ? "yes" : "no"}
                        onChange={e => setForm(prev => ({ ...prev, isActive: e.target.value === "yes" }))}
                        label="Registro activo"
                      >
                        <MenuItem value="yes">Activo</MenuItem>
                        <MenuItem value="no">Inactivo</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Estado licencia</InputLabel>
                      <Select
                        value={form.licenseStatus}
                        onChange={e => setForm(prev => ({ ...prev, licenseStatus: e.target.value }))}
                        label="Estado licencia"
                      >
                        <MenuItem value="active">Activo</MenuItem>
                        <MenuItem value="inactive">Inactivo</MenuItem>
                        <MenuItem value="blocked">Bloqueado</MenuItem>
                        <MenuItem value="not_found">No encontrado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Notas"
                multiline
                minRows={showAdvancedFields ? 3 : 2}
                value={form.notes}
                onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : form.id ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ClientHardwareAuthorizations;
